import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Sidebar from '../Sidebar';
import { ArrowRight, Users, LayoutDashboard, UserCheck,Zap,ClipboardList,TrendingUp,Award,FileStack,CalendarRange } from 'lucide-react';
import HelpCenter from '../HelpCenter';
import ModuloHeader from '../ModuloHeader';
import RHControleExperiencia from './controle-experiencia/RHControleExperiencia';
import RHAutomacoes from './automacao/RHAutomacoes';
import RHForms from './formularios/RHForms';
import RHTransicaoCargo from './transicao-cargo/RHTransicaoCargo';
import RHDestaqueMes from './destaque-mes/RHDestaqueMes';
import RHSolicitacoes from './solicitacoes/RHSolicitacoes';
import RHDimensionamentoEscala from './dimensionamento-escala/RHDimensionamentoEscala';
import { isUsuarioPresoLoja } from '../../utils/escopoUsuario';
import { perfilNoModulo } from '../../utils/perfisDerivados';

const PERMISSIONS = {
    'automacoes': ['RH', 'Admin', 'Diretoria'],
    'controle-experiencia': ['RH', 'Admin', 'Diretoria'],
    'transicao-cargo': ['RH', 'Admin', 'Diretoria', 'Treinamento', 'Operacional'],
    'forms': ['RH', 'Admin', 'Diretoria','EntrevistaDesligamento','Gerente', 'Operacional'],
    // Destaque do Mês: gerentes (titular e plantão) elegem/consultam a própria loja;
    // RH/Diretoria/Admin veem a rede toda (com filtro de loja).
    'destaque-mes': ['RH', 'Admin', 'Diretoria', 'Gerente', 'Gerente Adm', 'Gerente Plantao', 'Operacional'],
    // Solicitações: o balcão de documentos da loja para o RH (e o Cancelamento
    // de Contrato, que mora dentro dele desde 2026-09-15). A loja envia e
    // consulta a própria loja; RH/Diretoria tratam a rede; RH publica modelos.
    'solicitacoes': ['RH', 'Admin', 'Diretoria', 'Gerente', 'Gerente Adm', 'Gerente Plantao', 'Operacional', 'Coordenador de Qualidade'],
    // Dimensionamento de Escala: ferramenta de escritorio. O gerente de loja NAO
    // entra (decisao do Rodrigo, 14/09). `Diretoria RH` entra por heranca de
    // `Diretoria` -- perfil derivado nao se escreve em lista. `Operacional`
    // (Consultor de Operacoes) liberado em 15/10 (pedido do Rodrigo) -- ele ja
    // tinha acesso total ao modulo 'rh' no backend (nao esta nos excetoPrefixos),
    // so faltava o card aparecer aqui.
    'dimensionamento-escala': ['RH', 'Admin', 'Diretoria', 'Operacional']
    // PPI migrou para o módulo Treinamento (2026-07-15) — ver src/components/treinamento/Treinamento.js.
};

const modulesList = [
    {
        id: 'dimensionamento-escala',
        label: 'Dimensionamento de Escala',
        icon: CalendarRange,
        color: '#d81d1d',
        bgColor: '#fedbdb',
        desc: 'Monta a escala da loja numa grade de 24 horas: escolhe o turno, marca o break e recebe a curva de gente por hora, o efetivo e a tabela pronta. Versao datada e comparavel.',
        stat: 'Planejamento',
    },
    {
        id: 'automacoes',
        label: 'Automações',
        icon: Zap,
        color: '#d81d1d',
        bgColor: '#fedbdb',
        desc: 'Benefícios e folha: DIRF x eSocial, Benefício Social, Empréstimo Consignado, Vale Transporte, iFood, VR e Wellhub.',
        stat: 'Automações',
    },
    {
        id: 'controle-experiencia',
        label: 'Controle de Experiência',
        icon: UserCheck,
        color: '#d81d1d',
        bgColor: '#fedbdb',
        desc: 'Identifica ex-funcionários recontratados no mesmo cargo e empresa que não devem assinar novo contrato de experiência.',
        stat: 'Controle RH',
    },
    {
        id: 'transicao-cargo',
        label: 'Transição de Cargo',
        icon: TrendingUp,
        color: '#d81d1d',
        bgColor: '#fedbdb',
        desc: 'Promoções de Coordenador Q&S e Gerente de Plantão: elegibilidade (6 meses / anual), lançamento das notas e histórico.',
        stat: 'Carreira',
    },
    {
        id: 'forms',
        label: 'Formulários',
        icon: ClipboardList,
        color: '#db0707',
        bgColor: '#eec1c1',
        desc: 'Entrevista de Desligamento, Primeiros 15 Dias e Pesquisa de Experiência do Colaborador — preenchimento, consulta e relatórios.',
        stat: 'Formulários',
    },
    {
        id: 'destaque-mes',
        label: 'Destaque do Mês',
        icon: Award,
        color: '#d81d1d',
        bgColor: '#fedbdb',
        desc: 'Funcionários exemplares da loja (sem falta, atestado ou atraso no mês) — o gerente elege os destaques a partir do espelho de ponto.',
        stat: 'Reconhecimento',
    },
    {
        id: 'solicitacoes',
        label: 'Demandas para RH',
        icon: FileStack,
        color: '#d81d1d',
        bgColor: '#fedbdb',
        // ⚠️ Dez solicitações num card só, no molde do card de Formulários: nove
        // são DOCUMENTO (a loja baixa o modelo, preenche à mão ou assina, e
        // anexa) e o Término do Contrato de Experiência é a exceção de tela.
        desc: 'Documentos que a loja encaminha ao RH: alteração contratual, atualização de cadastro, Vale Transporte, advertência, falta de marcação, demissão, saída antecipada — e o Término do Contrato de Experiência.',
        stat: 'Documentos',
    }
];

const RHPainel = () => {
    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : {};
    const userRole = perfilNoModulo(user?.perfil, 'rh') || '';

    // Deep-link via querystring (ex.: vindo do card de pendências da Home):
    //   ?tab=forms&sub=p15-consulta  → abre a aba Formulários já na consulta dos Primeiros 15.
    const [searchParams] = useSearchParams();
    const tabInicial = searchParams.get('tab');
    const subInicial = searchParams.get('sub');

    const [isMobile] = useState(window.innerWidth <= 768);
    const [isCollapsed, setIsCollapsed] = useState(window.innerWidth <= 768);
    const [activeTab, setActiveTab] = useState(tabInicial || 'dashboard');
    const [isHelpOpen, setIsHelpOpen] = useState(false);

    // Usuário de loja (ex.: Linhares) não vê os sub-módulos centrais; só Formulários.
    const RESTRITOS_LOJA = ['automacoes', 'controle-experiencia', 'transicao-cargo'];
    const presoLoja = isUsuarioPresoLoja(user);
    const allowedModules = modulesList
        .filter(mod =>
            PERMISSIONS[mod.id]?.includes(userRole) &&
            !(presoLoja && RESTRITOS_LOJA.includes(mod.id))
        )
        // Cards da Visão Geral (e abas) em ordem alfabética pelo nome do módulo.
        .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));

    const OverviewWidgets = () => (
        <div>
            <div style={{
                background: 'linear-gradient(135deg, #e11d48 0%, #d81d1d 100%)',
                borderRadius: '16px', padding: '40px', color: '#fff',
                marginBottom: '32px', position: 'relative', overflow: 'hidden',
                boxShadow: '0 10px 15px -3px rgba(216, 29, 29, 0.3)',
            }}>
                <div style={{ position: 'relative', zIndex: 10 }}>
                    <h2 style={{ fontSize: '32px', fontWeight: '800', marginBottom: '12px', marginTop: 0, color: '#fff' }}>
                        Recursos Humanos
                    </h2>
                    <p style={{ color: '#febfbf', fontSize: '18px', maxWidth: '650px', margin: 0, lineHeight: '1.6' }}>
                        Ferramentas de controle e gestão para o departamento de RH do Grupo Correia MC.
                    </p>
                </div>
                <div style={{ position: 'absolute', right: '-30px', top: '-40px', opacity: 0.12 }}>
                    <Users size={250} color="#ffffff" />
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px' }}>
                {allowedModules.map((mod) => (
                    <div
                        key={mod.id}
                        onClick={() => setActiveTab(mod.id)}
                        style={{
                            backgroundColor: '#fff', borderRadius: '16px', padding: '24px',
                            border: '1px solid #f0e2e2', display: 'flex', flexDirection: 'column',
                            height: '100%', cursor: 'pointer', position: 'relative', overflow: 'hidden',
                            transition: 'all 0.2s', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 20px -2px rgba(0,0,0,0.1)'; }}
                        onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0,0,0,0.05)'; }}
                    >
                        <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '4px', backgroundColor: mod.color }} />
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
                            <div>
                                <p style={{ fontSize: '12px', fontWeight: '800', textTransform: 'uppercase', color: mod.color, marginBottom: '8px', margin: 0 }}>{mod.stat}</p>
                                <h3 style={{ fontSize: '20px', fontWeight: '800', color: '#0f172a', margin: 0 }}>{mod.label}</h3>
                            </div>
                            <div style={{ padding: '12px', borderRadius: '12px', backgroundColor: mod.bgColor, color: mod.color }}>
                                <mod.icon size={26} />
                            </div>
                        </div>
                        <div style={{ marginTop: 'auto' }}>
                            <p style={{ fontSize: '14px', color: '#8b6464', fontWeight: '500', lineHeight: '1.5', margin: '0 0 16px 0' }}>{mod.desc}</p>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: '700', color: mod.color }}>
                                <span>Acessar</span> <ArrowRight size={16} />
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );

    useEffect(() => {
        const handleResize = () => { if (window.innerWidth <= 768) setIsCollapsed(true); };
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (activeTab !== 'dashboard' && !PERMISSIONS[activeTab]?.includes(userRole)) {
            setActiveTab('dashboard');
        }
    }, [activeTab, userRole]);

    const contentBoxStyle = {
        backgroundColor: '#fff', borderRadius: '16px', border: '1px solid #f0e2e2',
        padding: '32px', minHeight: '75vh', width: '100%',
    };

    const renderContent = () => {
        if (activeTab === 'dashboard') return <OverviewWidgets />;
        switch (activeTab) {
            case 'automacoes': return <div style={contentBoxStyle}><RHAutomacoes /></div>;
            case 'controle-experiencia': return <div style={contentBoxStyle}><RHControleExperiencia /></div>;
            case 'transicao-cargo': return <div style={contentBoxStyle}><RHTransicaoCargo /></div>;
            case 'forms': return <div style={contentBoxStyle}><RHForms subInicial={subInicial} /></div>;
            case 'destaque-mes': return <div style={contentBoxStyle}><RHDestaqueMes /></div>;
            case 'solicitacoes': return <div style={contentBoxStyle}><RHSolicitacoes /></div>;
            case 'dimensionamento-escala': return <div style={contentBoxStyle}><RHDimensionamentoEscala /></div>;
            default: return null;
        }
    };

    const getMarginLeft = () => window.innerWidth <= 768 ? '0px' : (isCollapsed ? '80px' : '260px');

    // Verbete do Manual que abre expandido conforme a aba ativa.
    // (O botão "Manual da Tela" mostra TODO o módulo RH; isto só escolhe o ponto de partida.)
    const getSecaoAjuda = () => {
        switch (activeTab) {
            case 'automacoes':           return 'rh-dirf-esocial';
            case 'controle-experiencia': return 'controle-experiencia';
            case 'transicao-cargo':      return 'transicao-cargo';
            case 'forms':                return 'rh-entrevista-desligamento';
            default:                     return 'visao-geral-rh';
        }
    };

    return (
        <div style={{ display: 'flex', minHeight: '100vh', backgroundColor: '#fcf8f8' }}>
            <Sidebar isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} mostrarBotaoMobile={false} />
            <div style={{ flex: 1, marginLeft: getMarginLeft(), transition: 'margin-left 0.3s ease' }}>

                {/* CABEÇALHO — padrão dos módulos, na cor do RH (vermelho) */}
                <ModuloHeader
                    eyebrow="Módulo"
                    titulo="Recursos Humanos"
                    cor="#d81d1d"
                    onAbrirManual={() => setIsHelpOpen(true)}
                    onAbrirMenu={() => setIsCollapsed(false)}
                    isMobile={isMobile}
                />

                <div style={{ padding: '40px', maxWidth: '1600px', margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>

                    {/* BARRA DE NAVEGAÇÃO */}
                    <div style={{
                        display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '32px',
                        backgroundColor: '#fff', padding: '8px', borderRadius: '12px',
                        border: '1px solid #f0e2e2', width: '100%',
                    }}>
                        <button
                            onClick={() => setActiveTab('dashboard')}
                            style={{
                                display: 'flex', alignItems: 'center', gap: '8px',
                                padding: '10px 20px', borderRadius: '8px', border: 'none',
                                fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s',
                                background: activeTab === 'dashboard' ? 'linear-gradient(135deg, #f63b3b 0%, #d81d1d 100%)' : 'transparent',
                                color: activeTab === 'dashboard' ? '#fff' : '#64748b',
                                boxShadow: activeTab === 'dashboard' ? '0 4px 6px rgba(216, 29, 29, 0.3)' : 'none',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            <LayoutDashboard size={18} /> Visão Geral
                        </button>

                        <div style={{ minWidth: '1px', backgroundColor: '#f0e2e2', margin: '0 5px' }} />

                        {allowedModules.map((mod) => (
                            <button
                                key={mod.id}
                                onClick={() => setActiveTab(mod.id)}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: '8px',
                                    padding: '10px 20px', borderRadius: '8px', fontWeight: '600',
                                    cursor: 'pointer', transition: 'all 0.2s',
                                    border: activeTab === mod.id ? `2px solid ${mod.color}` : '2px solid transparent',
                                    backgroundColor: activeTab === mod.id ? mod.bgColor : 'transparent',
                                    color: activeTab === mod.id ? mod.color : '#64748b',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                <mod.icon size={18} /> {mod.label}
                            </button>
                        ))}
                    </div>

                    {renderContent()}
                </div>
            </div>

            <HelpCenter
                isOpen={isHelpOpen}
                onClose={() => setIsHelpOpen(false)}
                secaoInicial={getSecaoAjuda()}
                modulo="rh"
            />
        </div>
    );
};

export default RHPainel;
